import React from 'react';
import {
  IconBell,
  IconChevronDown,
  IconDoorEnter,
  IconPhone,
  IconCalendar,
  IconPrinter,
  IconSettings,
  type Icon,
} from '@tabler/icons-react';
import { getInitials, type AvatarColour } from '../styles/avatarPalette';
import styles from './Header.module.css';

interface UtilityNavItem {
  /** One of the nav route keys MainLayout maps in NAV_ROUTE_TO_VIEW. */
  key: string;
  label: string;
  Icon: Icon;
}

/* Destinations that live in the topbar rather than the tab strip below it:
   occasional utilities, not surfaces anyone works in all day. Moving these four
   up here is what lets AppNav's remaining nine tabs fit a 1280px viewport at
   full label size — all 13 needed ~1630px and pushed the page sideways. It also
   follows the bell, which has always navigated to a view that has no tab. */
const UTILITY_NAV_ITEMS: UtilityNavItem[] = [
  /* New check-in leads the cluster. Unlike the other four it is an action, not
     a destination, and it used to exist only as a toolbar button on the work
     item dashboard — so reception had to go back to the dashboard before they
     could start a check-in from anywhere else in the app. The glyph matches
     ToolbarActionGroup's Check-in button, which is still there. */
  { key: 'new-check-in', label: 'New check-in', Icon: IconDoorEnter },
  { key: 'calls', label: 'Calls', Icon: IconPhone },
  { key: 'calendar', label: 'Calendar', Icon: IconCalendar },
  { key: 'print', label: 'Print', Icon: IconPrinter },
  { key: 'admin', label: 'Admin', Icon: IconSettings },
];

interface HeaderProps {
  /** Displayed in the topbar and used to derive the 2-character avatar initials */
  userName: string;
  /** Resolved avatar colour for the signed-in user (locked palette). */
  avatarColor?: AvatarColour;
  /** Active nav route key, shared with AppNav so exactly one item reads as current. */
  activeRoute: string;
  /** Navigate to a utility destination — same route keys AppNav emits. */
  onNavChange: (route: string) => void;
  /** Called when the bell icon is clicked */
  onBellClick: () => void;
  /** Called when the signed-in user's avatar / name is clicked — opens Settings. */
  onAccountClick: () => void;
  /** True while the Settings page is showing, so the account button reads as current. */
  accountActive?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  userName,
  avatarColor,
  activeRoute,
  onNavChange,
  onBellClick,
  onAccountClick,
  accountActive = false,
}) => {
  return (
    <header className={styles.header}>
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <img src="/logo.png" alt="TechSolvers" className={styles.brandLogo} />
          <span className={styles.brandSub}>· SM4RT</span>
        </div>

        <div className={styles.topbarRight}>
          <nav className={styles.utilityNav} aria-label="Utilities">
            {UTILITY_NAV_ITEMS.map(({ key, label, Icon }) => {
              const active = key === activeRoute;
              return (
                <button
                  type="button"
                  key={key}
                  className={`${styles.utilityButton}${active ? ` ${styles.utilityButtonActive}` : ''}`}
                  onClick={() => onNavChange(key)}
                  /* The label is the accessible name now that it isn't rendered,
                     and the title is the only thing that says what the icon is. */
                  aria-label={label}
                  title={label}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon size={22} stroke={1.5} />
                </button>
              );
            })}
          </nav>

          <span className={styles.utilityDivider} aria-hidden="true" />

          <button
            type="button"
            className={styles.bellButton}
            onClick={onBellClick}
            aria-label="Notifications"
          >
            <IconBell size={22} stroke={1.5} />
          </button>
          <button
            type="button"
            className={`${styles.accountButton}${accountActive ? ` ${styles.accountButtonActive}` : ''}`}
            onClick={onAccountClick}
            aria-label={`${userName} — settings`}
            /* The name is hidden on narrow screens; this keeps it a tap/hover away. */
            title={`${userName} — settings`}
            aria-current={accountActive ? 'page' : undefined}
          >
            <span
              className={styles.avatar}
              aria-hidden="true"
              style={avatarColor ? { backgroundColor: avatarColor.bg, color: avatarColor.fg } : undefined}
            >
              {getInitials(userName)}
            </span>
            <span className={styles.userName}>{userName}</span>
            <span className={styles.chevron}>
              <IconChevronDown size={17} stroke={1.5} />
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};
